<script lang="ts" setup>
import type { NavItem } from '~kui/types/navigation';

import { Icon } from '~ds/components/ui/icon';
import { Heading, Text } from '~ds/components/ui/typography';

interface Props {
  item: NavItem;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  click: [item: NavItem];
}>();

const component = computed<string | object>(() => {
  if (props.item.to) {
    return defineNuxtLink({});
  }

  if (props.item.onClick) {
    return 'button';
  }

  return 'div';
});

const clickHandler = async (event: Event) => {
  // Prevent default navigation for anchor links to allow custom scroll behavior
  if (props.item.to && props.item.to.startsWith('#')) {
    event.preventDefault();
  }

  if (component.value === 'button' && props.item.onClick) {
    await props.item.onClick();
  }

  emit('click', props.item);
};
</script>

<template>
  <component
    :is="component"
    class="flex gap-4 cursor-pointer items-center hover:opacity-50 transition-opacity duration-200 w-full"
    :to="item.to"
    :target="item.target"
    :rel="item.target && 'noopener noreferrer'"
    :data-testid="item.testId"
    @click="clickHandler"
  >
    <Icon
      v-if="item.iconPath"
      :name="item.iconPath"
      size="lg"
    />
    <!-- `fit="inside"` letterboxes rather than cropping: the default `cover` resize trims
         any illustration that isn't square. The box stays square so labels line up. -->
    <NuxtImg
      v-else-if="item.imagePath"
      width="48"
      height="48"
      fit="inside"
      :src="item.imagePath"
      alt=""
      role="presentation"
      class="shrink-0 size-12 object-contain"
    />
    <Heading
      v-if="item.imagePath"
      as="span"
      size="lg"
      class="text-left"
    >
      {{ item.label }}
    </Heading>
    <Text v-else size="lg">
      {{ item.label }}
    </Text>
  </component>
</template>
